import { createStore, del, get, set, setMany, update, UseStore, values } from 'idb-keyval/dist/esm-compat';

import { Api } from '../../typings/api';
import Utils from '../index';
import { LocalStorageDownloadInfos } from '../../containers/DownloadManager/actions';
import { DownloadGroupName, DownloadGroupStatus, SelectedDownloadsManagerItemState } from '../../containers/DownloadManager/reducer';
import { MultiAppAuthenticationCredentials } from '../../containers/MultiAppAuthentication/reducer';
import { OfflineManagerState } from '../../containers/OfflineManager/reducer';
import { ListDisplayType } from '../../containers/DriveItemList/constants';
import { GalleryInfosState } from '../../containers/Gallery/reducer';
import { DriveListState } from '../../containers/DriveItemList/reducer';
import { AuthenticationUserState } from '../../containers/Authentication/reducer';
import { PendingTasksManagerState } from '../../containers/PendingTasksManager/reducer';
import { TrackingData } from '../statistics/typings';
import { Server } from '../../containers/MultiAppAuthentication/constants';
import { StoredReadStatusInfo } from '../../containers/ReadStatus/actions';
import { StoredNewItemInfo } from '../../containers/DriveItemList/New/actions';
import { StoredDownloadGroups } from '../../containers/DownloadManager/actions/store';

class IndexedDBManager {
	private static prefix: string;
	private static NOTES_DB_NAME = 'gallApp-notes';
	private static NOTES_STORE_NAME = 'notes';
	private static UPREZ_DB_NAME = 'UprezAppDB';
	private static UPREZ_STORE_NAME = 'uprezAppStore';
	private static UPREZ_STORE: UseStore = IndexedDBManager.openStore(IndexedDBManager.UPREZ_DB_NAME, IndexedDBManager.UPREZ_STORE_NAME, false);
	private static isIndexedDBInitialized = 'uprez-isIndexedDBInitialized';
	private static downloadsKey = 'uprez-downloads';
	private static downloadDownloadGroupsKey = 'uprez-downloadGroups';
	private static pendingSelectedDownloadsKey = 'uprez-pendingSelectedDownloads';
	private static multiAppCredentialsKey = 'uprez-multiAppCredentials';
	private static galleryConfigKey = 'uprez-galleryConfig';
	private static preventAutologinKey = 'uprez-preventAutologin';
	private static downloadPopupDontAskAgainKey = 'uprez-downloadPopupDontAskAgain';
	private static storeOfflineManagerKey = 'uprez-offlineManager';
	private static displayTypeKey = 'uprez-driveItemListDisplayType';
	private static foldersDisplayTypeKey = 'uprez-foldersDisplayType';
	private static selectionKey = 'uprez-favorites';
	private static notesSelectionKey = 'uprez-selectedNotes';
	private static newItemsKey = 'uprez-newItems';
	private static readStatusKey = 'uprez-readStatus';
	private static galleryInfosKey = 'uprez-galleryInfos';
	private static driveListKey = 'uprez-driveList';
	private static userInfosKey = 'uprez-userInfos';
	private static profileInfosKey = 'uprez-profileInfos';
	private static multiAppServerKey = 'uprez-multiAppServer';
	private static trackingQueueKey = 'uprez-trackingQueue';
	private static pendingTasksKey = 'uprez-pendingTasks';
	private static userLocaleKey = 'uprez-userLocale';

	public static setGalleryPrefix(int: number, login?: string): void {
		IndexedDBManager.prefix = Utils.getGalleryPrefix(int, login);
	}

	public static getGalleryPrefix(): string {
		return IndexedDBManager.prefix;
	}

	public static getNote(id: number): Promise<Api.NoteData | undefined> {
		const store = IndexedDBManager.openStore(IndexedDBManager.NOTES_DB_NAME, IndexedDBManager.NOTES_STORE_NAME);

		return IndexedDBManager.getItem<Api.NoteData>(id, store);
	}

	public static getNotes(): Promise<Api.NoteData[]> {
		const store = IndexedDBManager.openStore(IndexedDBManager.NOTES_DB_NAME, IndexedDBManager.NOTES_STORE_NAME);

		return IndexedDBManager.getAllItems<Api.NoteData>(store);
	}

	public static setNote(id: number, data: Api.NoteData): Promise<void> {
		const store = IndexedDBManager.openStore(IndexedDBManager.NOTES_DB_NAME, IndexedDBManager.NOTES_STORE_NAME);

		return IndexedDBManager.setItem(id, data, store);
	}

	public static setNotes(notes: Api.NoteData[]): Promise<void> {
		const store = IndexedDBManager.openStore(IndexedDBManager.NOTES_DB_NAME, IndexedDBManager.NOTES_STORE_NAME);
		const entries: [number, Api.NoteData][] = [];
		notes.forEach(note => entries.push([note.id, note]));

		return IndexedDBManager.setItems(entries, store);
	}

	public static updateNote(id: number, newData: Api.NoteData): Promise<void> {
		const store = IndexedDBManager.openStore(IndexedDBManager.NOTES_DB_NAME, IndexedDBManager.NOTES_STORE_NAME);
		const updater = (oldData: Api.NoteData) => ({ ...oldData, ...newData });

		return IndexedDBManager.updateItem(id, updater, store);
	}

	public static removeNote(id: number): Promise<void> {
		const store = IndexedDBManager.openStore(IndexedDBManager.NOTES_DB_NAME, IndexedDBManager.NOTES_STORE_NAME);

		return IndexedDBManager.removeItem(id, store);
	}

	// Initial access
	public static async wasIndexedDBInitialized(isPerApp: boolean): Promise<boolean | null> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.isIndexedDBInitialized, isPerApp);

		try {
			const isInitialized = await IndexedDBManager.getItem<boolean>(storageKey, IndexedDBManager.UPREZ_STORE);

			return isInitialized || false;
		} catch (error) {
			console.error('Failed to get initial access from IndexedDB:', error);
			return null;
		}
	}

	public static async setIndexedDBInitialized(isPerAppContainer: boolean): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.isIndexedDBInitialized, isPerAppContainer);

		try {
			await IndexedDBManager.setItem(storageKey, true, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set initial access in IndexedDB:', error);
			throw error;
		}
	}

	// Downloads
	public static async getDownloads(): Promise<LocalStorageDownloadInfos[] | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.downloadsKey, true);

		try {
			return await IndexedDBManager.getItem<LocalStorageDownloadInfos[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get downloads from IndexedDB:', error);
			return [];
		}
	}

	public static async setDownloads(downloads: LocalStorageDownloadInfos[]): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.downloadsKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, downloads, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set downloads in IndexedDB:', error);
			throw error;
		}
	}

	// Download groups
	public static async getDownloadGroups(): Promise<StoredDownloadGroups | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.downloadDownloadGroupsKey, true);

		try {
			return await IndexedDBManager.getItem<StoredDownloadGroups>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get download groups from IndexedDB:', error);
			return undefined;
		}
	}

	public static async setDownloadGroups(group: DownloadGroupName, status: DownloadGroupStatus): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.downloadDownloadGroupsKey, true);

		try {
			const downloadGroups = await IndexedDBManager.getDownloadGroups() || {
				all: DownloadGroupStatus.NotStarted,
				important: DownloadGroupStatus.NotStarted,
				selected: DownloadGroupStatus.NotStarted,
			};

			downloadGroups[group] = status;

			await IndexedDBManager.setItem(storageKey, downloadGroups, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set download groups in IndexedDB:', error);
			throw error;
		}
	}

	// Manual downloads
	public static async getPendingSelectedDownloads(): Promise<SelectedDownloadsManagerItemState[] | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.pendingSelectedDownloadsKey, true);

		try {
			return await IndexedDBManager.getItem<SelectedDownloadsManagerItemState[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get pending selected downloads from IndexedDB:', error);
			return [];
		}
	}

	public static async setPendingSelectedDownloads(pendingSelectedDownloads: SelectedDownloadsManagerItemState[]): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.pendingSelectedDownloadsKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, pendingSelectedDownloads, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set pending selected downloads in IndexedDB:', error);
			throw error;
		}
	}

	// MultiAppCredentials
	public static async setMultiAppCredentials(credentials: MultiAppAuthenticationCredentials): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.multiAppCredentialsKey, false);

		try {
			await IndexedDBManager.setItem(storageKey, credentials, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set credentials in IndexedDB:', error);
			throw error;
		}
	}

	public static async getMultiAppCredentials(): Promise<MultiAppAuthenticationCredentials | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.multiAppCredentialsKey, false);

		try {
			return await IndexedDBManager.getItem<MultiAppAuthenticationCredentials>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get credentials from IndexedDB:', error);
			return null;
		}
	}

	public static async removeMultiAppCredentials(): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.multiAppCredentialsKey, false);

		try {
			await IndexedDBManager.removeItem(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to remove credentials from IndexedDB:', error);
			throw error;
		}
	}

	// GalleryConfig
	public static async setGalleryConfig(storeGalleryConfig: Api.Config): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.galleryConfigKey, false);

		try {
			await IndexedDBManager.setItem(storageKey, storeGalleryConfig, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set gallery config in IndexedDB:', error);
			throw error;
		}
	}

	public static async getGalleryConfig(): Promise<Api.Config | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.galleryConfigKey, false);

		try {
			return await IndexedDBManager.getItem<Api.Config>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get gallery config from IndexedDB:', error);
			return null;
		}
	}

	public static async removeGalleryConfig(): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.galleryConfigKey, false);

		try {
			await IndexedDBManager.removeItem(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to remove gallery config from IndexedDB:', error);
			throw error;
		}
	}

	public static async setPreventAutologin(prevent: boolean): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.preventAutologinKey, false);

		try {
			if (prevent) {
				await IndexedDBManager.setItem(storageKey, true, IndexedDBManager.UPREZ_STORE);
			} else {
				await IndexedDBManager.removeItem(storageKey, IndexedDBManager.UPREZ_STORE);
			}
		} catch (error) {
			console.error('Failed to set prevent autologin flag in IndexedDB:', error);
			throw error;
		}
	}

	public static async getPreventAutologin(): Promise<boolean> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.preventAutologinKey, false);

		try {
			const value = await IndexedDBManager.getItem<boolean>(storageKey, IndexedDBManager.UPREZ_STORE);
			return value === true;
		} catch (error) {
			console.error('Failed to get prevent autologin flag from IndexedDB:', error);
			return false;
		}
	}

	// Don't ask again for download popup
	public static async getDownloadPopupDontAskAgain(): Promise<boolean | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.downloadPopupDontAskAgainKey, true);

		try {
			return await IndexedDBManager.getItem<boolean>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get DownloadPopupDontAskAgain from IndexedDB:', error);
			return undefined;
		}
	}

	public static async setDownloadPopupDontAskAgain(): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.downloadPopupDontAskAgainKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, true, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set DownloadPopupDontAskAgain in IndexedDB:', error);
			throw error;
		}
	}

	// OfflineManager
	public static async getStoreOfflineManager(): Promise<OfflineManagerState | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.storeOfflineManagerKey, true);

		try {
			return await IndexedDBManager.getItem<OfflineManagerState>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get OfflineManager from IndexedDB:', error);
			return undefined;
		}
	}

	public static async setStoreOfflineManager(offlineManager: OfflineManagerState): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.storeOfflineManagerKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, offlineManager, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set OfflineManager in IndexedDB:', error);
			throw error;
		}
	}

	// DisplayType
	public static async getDisplayType(): Promise<ListDisplayType | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.displayTypeKey, true);

		try {
			return await IndexedDBManager.getItem<ListDisplayType>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get ListDisplayType from IndexedDB:', error);
			return undefined;
		}
	}

	public static async setDisplayType(displayType: ListDisplayType): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.displayTypeKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, displayType, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set ListDisplayType in IndexedDB:', error);
			throw error;
		}
	}

	// FoldersDisplayType
	// Folders displayType (is set via dropdown inside each folder and has priority over application or backoffice displayType)
	public static async getFoldersDisplayType(): Promise<{ [k: string]: ListDisplayType } | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.foldersDisplayTypeKey, true);

		try {
			return await IndexedDBManager.getItem<{ [k: string]: ListDisplayType }>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get FoldersDisplayType from IndexedDB:', error);
			return undefined;
		}
	}

	public static async setFolderDisplayType(folderId: number | string, displayType: ListDisplayType): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.foldersDisplayTypeKey, true);

		try {
			let foldersDisplayType = await IndexedDBManager.getFoldersDisplayType();

			if (!foldersDisplayType) {
				foldersDisplayType = {
					[folderId]: displayType,
				};
			} else {
				foldersDisplayType[folderId] = displayType;
			}

			await IndexedDBManager.setItem(storageKey, foldersDisplayType, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set FoldersDisplayType in IndexedDB:', error);
			throw error;
		}
	}

	public static async resetFoldersDisplayType(): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.foldersDisplayTypeKey, true);

		try {
			await IndexedDBManager.removeItem(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to reset FoldersDisplayType in IndexedDB:', error);
			throw error;
		}
	}

	// Selection
	public static async getSelection(): Promise<number[] | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.selectionKey, true);

		try {
			return await IndexedDBManager.getItem<number[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get selection from IndexedDB:', error);
			return null;
		}
	}

	public static async setSelection(selection: number[]): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.selectionKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, selection, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set selection in IndexedDB:', error);
			throw error;
		}
	}

	public static async getNotesSelection(): Promise<number[] | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.notesSelectionKey, true);

		try {
			return await IndexedDBManager.getItem<number[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get notes selection from IndexedDB:', error);
			return null;
		}
	}

	public static async setNotesSelection(selection: number[]): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.notesSelectionKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, selection, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set notes selection in IndexedDB:', error);
			throw error;
		}
	}

	// New items
	public static async getNewItems(): Promise<StoredNewItemInfo[] | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.newItemsKey, true);

		try {
			return await IndexedDBManager.getItem<StoredNewItemInfo[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get new items from IndexedDB:', error);
			return null;
		}
	}

	public static async setNewItems(newItems: StoredNewItemInfo[]): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.newItemsKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, newItems, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set new items in IndexedDB:', error);
			throw error;
		}
	}

	// Read status
	public static async getReadStatus(): Promise<StoredReadStatusInfo[] | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.readStatusKey, true);

		try {
			return await IndexedDBManager.getItem<StoredReadStatusInfo[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get read status from IndexedDB:', error);
			return null;
		}
	}

	public static async setReadStatus(readStatus: StoredReadStatusInfo[]): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.readStatusKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, readStatus, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set read status in IndexedDB:', error);
			throw error;
		}
	}

	// Gallery infos
	public static async getGalleryInfos(): Promise<GalleryInfosState | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.galleryInfosKey, true);

		try {
			return await IndexedDBManager.getItem<GalleryInfosState>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get gallery infos from IndexedDB:', error);
			return null;
		}
	}

	public static async setGalleryInfos(galleryInfos: GalleryInfosState): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.galleryInfosKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, galleryInfos, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set gallery infos in IndexedDB:', error);
			throw error;
		}
	}

	// Drive list
	public static async getStoreDriveList(): Promise<DriveListState | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.driveListKey, true);

		try {
			return await IndexedDBManager.getItem<DriveListState>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get drive list from IndexedDB:', error);
			return null;
		}
	}

	public static async setStoreDriveList(driveList: DriveListState): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.driveListKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, driveList, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set drive list in IndexedDB:', error);
			throw error;
		}
	}

	// Authentication credentials
	public static async setCredentials(credentials: AuthenticationUserState): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.userInfosKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, credentials, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set credentials in IndexedDB:', error);
			throw error;
		}
	}

	public static async getCredentials(): Promise<AuthenticationUserState | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.userInfosKey, true);

		try {
			return await IndexedDBManager.getItem<AuthenticationUserState>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get credentials from IndexedDB:', error);
			return null;
		}
	}

	public static async removeCredentials(): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.userInfosKey, true);

		try {
			await IndexedDBManager.removeItem(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to remove credentials from IndexedDB:', error);
			throw error;
		}
	}

	// Profile infos
	public static async setProfileInfos(profileInfos: Api.ProfileData): Promise<void> {
		try {
			await IndexedDBManager.setItem(IndexedDBManager.profileInfosKey, profileInfos, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set profile infos in IndexedDB:', error);
			throw error;
		}
	}

	public static async getProfileInfos(): Promise<Api.ProfileData | null | undefined> {
		try {
			return await IndexedDBManager.getItem<Api.ProfileData>(IndexedDBManager.profileInfosKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get profile infos from IndexedDB:', error);
			return null;
		}
	}

	public static async removeProfileInfos(): Promise<void> {
		try {
			await IndexedDBManager.removeItem(IndexedDBManager.profileInfosKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to remove profile infos from IndexedDB:', error);
			throw error;
		}
	}

	// Tracking queue
	public static async getTrackingQueue(): Promise<TrackingData[] | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.trackingQueueKey, true);

		try {
			return await IndexedDBManager.getItem<TrackingData[]>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get tracking queue from IndexedDB:', error);
			return null;
		}
	}

	public static async setTrackingQueue(trackingQueue: TrackingData[]): Promise<void> {
		if (!IndexedDBManager.prefix) {
			return;
		}

		const storageKey = IndexedDBManager.getKey(IndexedDBManager.trackingQueueKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, trackingQueue, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set tracking queue in IndexedDB:', error);
			throw error;
		}
	}

	public static async removeTrackingQueue(): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.trackingQueueKey, true);

		try {
			await IndexedDBManager.removeItem(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to remove tracking queue from IndexedDB:', error);
			throw error;
		}
	}

	// Pending tasks
	public static async getPendingTasks(): Promise<PendingTasksManagerState | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.pendingTasksKey, true);

		try {
			return await IndexedDBManager.getItem<PendingTasksManagerState>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get pending tasks from IndexedDB:', error);
			return null;
		}
	}

	public static async setPendingTasks(pendingTasks: PendingTasksManagerState): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.pendingTasksKey, true);

		try {
			await IndexedDBManager.setItem(storageKey, pendingTasks, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set pending tasks in IndexedDB:', error);
			throw error;
		}
	}

	// User locale
	public static async getUserLocale(hasPrefix: boolean): Promise<string | null | undefined> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.userLocaleKey, hasPrefix);

		try {
			return await IndexedDBManager.getItem<string>(storageKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get locale from IndexedDB:', error);
			return null;
		}
	}

	public static async setUserLocale(locale: string, hasPrefix: boolean): Promise<void> {
		const storageKey = IndexedDBManager.getKey(IndexedDBManager.userLocaleKey, hasPrefix);

		try {
			await IndexedDBManager.setItem(storageKey, locale, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set locale in IndexedDB:', error);
			throw error;
		}
	}

	// Multi-app server
	public static async setMultiAppServer(server: Server): Promise<void> {
		try {
			await IndexedDBManager.setItem(IndexedDBManager.multiAppServerKey, server, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to set multi-app server in IndexedDB:', error);
			throw error;
		}
	}

	public static async getMultiAppServer(): Promise<Server | null | undefined> {
		try {
			return await IndexedDBManager.getItem<Server>(IndexedDBManager.multiAppServerKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to get multi-app server from IndexedDB:', error);
			return null;
		}
	}

	public static async removeMultiAppServer(): Promise<void> {
		try {
			await IndexedDBManager.removeItem(IndexedDBManager.multiAppServerKey, IndexedDBManager.UPREZ_STORE);
		} catch (error) {
			console.error('Failed to remove multi-app server from IndexedDB:', error);
			throw error;
		}
	}

	private static openStore(dbName: string, storeName: string, hasPrefix: boolean = true): UseStore {
		// opens existing store or creates a new if it doesn't exist
		return createStore(hasPrefix ? `${this.prefix}-${dbName}` : `${dbName}`, storeName);
	}

	private static getItem<T>(key: IDBValidKey, store: UseStore): Promise<T | undefined> {
		return get(key, store);
	}

	private static getAllItems<T>(store: UseStore): Promise<T[]> {
		return values(store);
	}

	// tslint:disable-next-line no-any
	private static setItem(key: IDBValidKey, value: any, store: UseStore): Promise<void> {
		return set(key, value, store);
	}

	// tslint:disable-next-line no-any
	private static setItems(items: [number, any][], store: UseStore): Promise<void> {
		return setMany(items, store);
	}

	private static updateItem<T>(key: IDBValidKey, updater: (oldValue: T | undefined) => T, store: UseStore): Promise<void> {
		return update(key, updater, store);
	}

	private static removeItem(key: IDBValidKey, store: UseStore): Promise<void> {
		return del(key, store);
	}

	private static getKey(key: string, needsPrefix: boolean): string {
		return needsPrefix ? `${IndexedDBManager.prefix}-${key}` : key;
	}
}

// Expose setDownloads to window for console access
if (typeof window !== 'undefined') {
	// tslint:disable-next-line:no-any
	(window as any).setDownloads = (downloads: LocalStorageDownloadInfos[]) => {
		return IndexedDBManager.setDownloads(downloads);
	};
}

export default IndexedDBManager;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/indexedDB/manager.ts